import { collection, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../lib/firebase';
import { 
  ShippixCredentials, 
  ShippixAuthResponse, 
  ShippixShipmentsResponse,
  ShippixCreateShipment,
  ShippixCreateResponse,
  ShippixPriceCalcRequest,
  ShippixPriceCalcResponse,
  ShippixService as ShippixServiceType
} from '../types/shippix';

export class ShippixService {
  private baseUrl: string;
  private tokenCache: Map<string, { token: string; expiry: number }>;
  private static instance: ShippixService | null = null;
  private currentUserId: string | null = null;

  private constructor() {
    this.baseUrl = '/shippix-api';
    this.tokenCache = new Map();
    this.currentUserId = null;
  }

  public static getInstance(): ShippixService {
    if (!ShippixService.instance) {
      ShippixService.instance = new ShippixService();
    }
    return ShippixService.instance;
  }

  public static resetInstance(): void {
    if (ShippixService.instance) {
      ShippixService.instance.tokenCache.clear();
      ShippixService.instance.currentUserId = null;
    }
    ShippixService.instance = null;
  }

  private async ensureUserContext(companyId: string): Promise<void> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const userId = currentUser?.uid || null;

    // Se o usuário mudou, limpar o cache
    if (this.currentUserId !== userId) {
      console.log('👤 Usuário mudou, limpando cache...');
      this.tokenCache.clear();
      this.currentUserId = userId;
    }
  }

  async getConfig(companyId: string): Promise<ShippixCredentials> {
    try {
      if (!companyId) {
        throw new Error('CompanyId é obrigatório para buscar configurações do Shippix');
      }

      await this.ensureUserContext(companyId);
      
      const docRef = doc(db, 'shippixConfigs', companyId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          grant_type: data.grant_type,
          client_id: data.client_id,
          client_secret: data.client_secret,
          username: data.username,
          password: data.password
        };
      }
      throw new Error('Configurações do Shippix não encontradas. Por favor, configure suas credenciais primeiro.');
    } catch (error) {
      console.error('Erro ao buscar configuração do Shippix:', error);
      throw error;
    }
  }

  async saveConfig(companyId: string, config: ShippixCredentials): Promise<void> {
    try {
      await this.ensureUserContext(companyId);

      const docRef = doc(db, 'shippixConfigs', companyId);
      await setDoc(docRef, {
        ...config,
        companyId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Limpar o cache para forçar nova autenticação
      this.tokenCache.delete(companyId);
    } catch (error) {
      console.error('Erro ao salvar configuração do Shippix:', error);
      throw error;
    }
  }

  private async authenticate(credentials: ShippixCredentials): Promise<ShippixAuthResponse> {
    try {
      // Limpar cache antes de tentar nova autenticação
      this.tokenCache.clear();

      console.log('🔑 Iniciando autenticação Shippix...');

      // Usar URLSearchParams como especificado na documentação
      const formData = new URLSearchParams();
      formData.append('grant_type', credentials.grant_type);
      formData.append('client_id', credentials.client_id);
      formData.append('client_secret', credentials.client_secret);
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      console.log('📤 Enviando dados de autenticação:', {
        grant_type: credentials.grant_type,
        client_id: credentials.client_id?.substring(0, 3) + '...',
        username: credentials.username
      });

      const response = await fetch(`${this.baseUrl}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: formData.toString()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro na autenticação Shippix:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          headers: Object.fromEntries(response.headers.entries())
        });
        
        let errorMessage = 'Falha na autenticação com o Shippix. Verifique suas credenciais.';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Se não conseguir parsear o JSON, usa a mensagem padrão
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('✅ Autenticação Shippix bem sucedida');
      return data;
    } catch (error) {
      console.error('❌ Erro crítico na autenticação Shippix:', error);
      // Limpar cache em caso de erro
      this.tokenCache.clear();
      throw error;
    }
  }

  private async getValidToken(companyId: string): Promise<string> {
    try {
      console.log('🔍 Verificando token existente...');
      const cachedData = this.tokenCache.get(companyId);
      const now = Date.now();

      // Se não houver token ou estiver expirado ou estiver próximo de expirar (5 minutos)
      if (!cachedData || now > (cachedData.expiry - 5 * 60 * 1000)) {
        console.log('🔄 Token não encontrado ou próximo de expirar, buscando configurações...');
        const config = await this.getConfig(companyId);

        console.log('✨ Configurações encontradas, iniciando autenticação...');
        const authResponse = await this.authenticate(config);
        
        // Guardar token com tempo de expiração
        this.tokenCache.set(companyId, {
          token: authResponse.access_token,
          expiry: Date.now() + (authResponse.expires_in * 1000)
        });

        return authResponse.access_token;
      }

      console.log('✅ Token válido encontrado em cache');
      return cachedData.token;
    } catch (error) {
      console.error('❌ Erro ao obter token:', error);
      // Limpar cache em caso de erro
      this.tokenCache.delete(companyId);
      throw error;
    }
  }

  async listShipments(companyId: string, page: number = 1, perPage: number = 25, includePickups: boolean = false): Promise<ShippixShipmentsResponse> {
    try {
      const token = await this.getValidToken(companyId);
      if (!token) {
        throw new Error('Configurações do Shippix não encontradas');
      }

      const queryParams = new URLSearchParams({
        page: Math.max(1, page).toString(), // API espera página começando em 1
        per_page: Math.max(1, perPage).toString(), // Ensure perPage is at least 1
        include_total: 'true' // Solicitar total de itens
      });

      const url = `${this.baseUrl}/v1/shipments/list?${queryParams}`;
      console.log('🚀 Chamando API Shippix - Listar Envios:', {
        url,
        params: { page: Math.max(1, page), per_page: perPage },
        token: `Bearer ${token.substring(0, 10)}...` // Mostrar apenas parte do token por segurança
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Paginate': 'true' // Forçar paginação
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta da API:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Erro ao listar envios: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Erro ao listar envios:', error);
      throw error;
    }
  }

  async createShipment(companyId: string, shipment: ShippixCreateShipment): Promise<ShippixCreateResponse> {
    await this.ensureUserContext(companyId);
    const token = await this.getValidToken(companyId);
    if (!token) {
      throw new Error('Token não disponível');
    }

    try {
      // Manter o formato original dos códigos postais
      const shipmentData = {
        ...shipment,
        service: shipment.service.toUpperCase(),
        sender_country: (shipment.sender_country || 'PT').toLowerCase(),
        recipient_country: (shipment.recipient_country || 'PT').toLowerCase(),
        charge_price: shipment.charge_price?.toFixed(2) || "0.00"
      };

      console.log('🚀 Criando envio:', {
        url: `${this.baseUrl}/v1/shipments/store`,
        data: shipmentData
      });

      const response = await fetch(`${this.baseUrl}/v1/shipments/store`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify([shipmentData]) // API espera um array de envios
      });

      const data = await response.json();
      console.log('📦 Resposta da API:', data);

      // Se a resposta contém um array com erro
      if (Array.isArray(data) && data[0]?.[0]?.error) {
        throw new Error(data[0][0].message);
      }

      // Se a resposta não está ok
      if (!response.ok) {
        const errorMessage = data.message || 'Erro ao criar envio';
        console.error('❌ Erro na resposta:', {
          status: response.status,
          statusText: response.statusText,
          data
        });
        throw new Error(errorMessage);
      }

      // A API retorna um array, pegamos o primeiro item
      const createdShipment = Array.isArray(data) ? data[0] : data;

      return {
        error: createdShipment.error || '',
        message: createdShipment.message || 'Envio criado com sucesso',
        tracking_code: createdShipment.tracking_code
      };
    } catch (error) {
      console.error('❌ Erro ao criar envio:', error);
      throw error;
    }
  }

  async getShipmentById(companyId: string, shipmentId: number) {
    await this.ensureUserContext(companyId);
    try {
      const token = await this.getValidToken(companyId);

      const response = await fetch(
        `${this.baseUrl}/v1/shipments/${shipmentId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Erro ao buscar detalhes do envio';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Se não conseguir parsear o JSON, usa a mensagem padrão
        }
        
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar detalhes do envio:', error);
      throw error;
    }
  }

  async getShipmentHistory(companyId: string, trackingCode: string): Promise<any> {
    await this.ensureUserContext(companyId);
    const token = await this.getValidToken(companyId);
    if (!token) {
      throw new Error('Configurações do Shippix não encontradas');
    }

    try {
      console.log('🔍 Chamando API Shippix - Histórico de Tracking:', {
        url: `${this.baseUrl}/v1/shipments/history/massive`,
        params: { trackings: trackingCode }
      });

      const response = await fetch(
        `${this.baseUrl}/v1/shipments/history/massive?trackings=${trackingCode}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro ao obter histórico:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error('Erro ao obter histórico do envio');
      }

      const data = await response.json();
      console.log('✅ Histórico de Tracking:', {
        tracking: trackingCode,
        data: data,
        headers: Object.fromEntries(response.headers.entries())
      });

      return data[trackingCode] || [];
    } catch (error) {
      console.error('❌ Erro ao obter histórico:', error);
      throw error;
    }
  }

  async calculatePrice(priceRequest: ShippixCreateShipment): Promise<ShippixPriceCalcResponse> {
    await this.ensureUserContext(priceRequest.companyId);
    const token = await this.getValidToken(priceRequest.companyId);
    if (!token) {
      throw new Error('Token não disponível');
    }

    if (!priceRequest.service_code) {
      throw new Error('Código do serviço é obrigatório');
    }

    try {
      // Calcular peso total e volumes
      let totalWeight = 0;
      let totalVolumes = 0;
      let totalVolumeM3 = 0;

      if (priceRequest.volume_items && priceRequest.volume_items.length > 0) {
        totalVolumes = priceRequest.volume_items.length;
        priceRequest.volume_items.forEach(volume => {
          totalWeight += Number(volume.weight) || 0;
          
          // Calcular volume em M3 e fator M3 para cada volume
          const length = Number(volume.length) || 0;
          const width = Number(volume.width) || 0;
          const height = Number(volume.height) || 0;
          
          // Volume em M3 = (C x L x A) / 1000000 (converter de cm³ para m³)
          const volumeM3 = (length * width * height) / 1000000;
          totalVolumeM3 += volumeM3;
        });
      }

      // Garantir valores mínimos
      totalWeight = Math.max(totalWeight, 0.1);
      totalVolumes = Math.max(totalVolumes, 1);

      // Calcular fator M3 = Volume em M3
      const fatorM3 = totalVolumeM3;

      const request = {
        service: priceRequest.service_code.toUpperCase(),
        sender_zip_code: priceRequest.sender_zip_code || '',
        sender_country: (priceRequest.sender_country || 'PT').toLowerCase(),
        recipient_zip_code: priceRequest.recipient_zip_code || '',
        recipient_country: (priceRequest.recipient_country || 'PT').toLowerCase(),
        volumes: totalVolumes.toString(),
        weight: totalWeight.toFixed(2),
        charge_price: priceRequest.charge_price?.toFixed(2) || "0.00",
        volume_m3: totalVolumeM3.toFixed(6),
        fator_m3: fatorM3.toFixed(6)
      };

      console.log('🚀 Chamando API Shippix - Calcular Preço:', {
        url: `${this.baseUrl}/v1/prices/calc`,
        dados: request
      });

      const response = await fetch(`${this.baseUrl}/v1/prices/calc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro ao calcular preço:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error('Erro ao calcular preço do envio');
      }

      const data = await response.json();
      console.log('✅ Preço calculado:', data);

      return {
        error: data.error || '',
        message: data.message || '',
        shipment_price: parseFloat(data.shipment_price || '0'),
        total_price: parseFloat(data.total_price || '0'),
        subtotal: parseFloat(data.subtotal || '0'),
        vat: parseFloat(data.vat || '0'),
        currency: data.currency || 'EUR',
        volume_m3: parseFloat(data.volume_m3 || '0'),
        fator_m3: parseFloat(data.fator_m3 || '0'),
        exceeded_weight: parseFloat(data.exceeded_weight || '0'),
        fees_price: parseFloat(data.fees_price || '0'),
        fuel_tax: parseFloat(data.fuel_tax || '0'),
        taxable_weight: parseFloat(data.taxable_weight || '0'),
        total: parseFloat(data.total || '0'),
        volumetric_weight: parseFloat(data.volumetric_weight || '0')
      };
    } catch (error) {
      console.error('❌ Erro ao calcular preço:', error);
      throw error;
    }
  }

  async listServices(companyId: string): Promise<ShippixServiceType[]> {
    await this.ensureUserContext(companyId);
    const token = await this.getValidToken(companyId);
    if (!token) {
      throw new Error('Configurações do Shippix não encontradas');
    }

    try {
      console.log('🚀 Chamando API Shippix - Listar Serviços:', {
        url: `${this.baseUrl}/v1/shipments/services/list`
      });

      const response = await fetch(`${this.baseUrl}/v1/shipments/services/list`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro na resposta da API:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        
        let errorMessage = 'Erro ao listar serviços';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Se não conseguir parsear o JSON, usa a mensagem padrão
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Erro ao listar serviços:', error);
      throw error;
    }
  }

  async getShipmentLabel(tracking: string, companyId: string): Promise<string> {
    const token = await this.getValidToken(companyId);
    if (!token) {
      throw new Error('Token não disponível');
    }

    const response = await fetch(`${this.baseUrl}/v1/shipments/${tracking}/labels`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Erro ao obter etiqueta');
    }

    const data = await response.json();
    return data.label;
  }

  async getShipmentGuide(tracking: string, companyId: string): Promise<string> {
    const token = await this.getValidToken(companyId);
    if (!token) {
      throw new Error('Token não disponível');
    }

    const response = await fetch(`${this.baseUrl}/v1/shipments/${tracking}/guide`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Erro na resposta da API:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`Erro ao obter guia de transporte: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Resposta da API Shippix (guide):', data);
    
    // Check if we have a URL
    if (data.url) {
      return data.url;
    }

    // The guide content comes in the label property
    if (!data.label || typeof data.label !== 'string') {
      console.error('Dados inválidos da API:', data);
      throw new Error('Guia de transporte inválida ou não encontrada');
    }

    // Remove any whitespace and ensure proper base64 padding
    const base64Data = data.label.trim().replace(/\s/g, '');
    if (!base64Data.match(/^[A-Za-z0-9+/]*={0,2}$/)) {
      console.error('Base64 inválido:', base64Data.substring(0, 100) + '...');
      throw new Error('Formato da guia de transporte inválido');
    }

    return base64Data;
  }

  async getShipmentCargoManifest(tracking: string, companyId: string): Promise<string> {
    try {
      const token = await this.getValidToken(companyId);
      
      // Endpoint correto para o manifesto de carga
      const response = await fetch(`${this.baseUrl}/public/shipments/${tracking}/cargo-manifest`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta do manifesto:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error('Erro ao obter manifesto de carga');
      }

      const data = await response.json();
      return data.source;
    } catch (error) {
      console.error('Erro ao obter manifesto de carga:', error);
      throw error;
    }
  }
}

export const shippixService = ShippixService.getInstance();
